import { useNavigate, useParams } from "react-router-dom";
import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import Breadcrumb from "../../../components/common/Breadcrumbs";
import { Tabs } from "react-simple-tabs-component";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";
import MemberTab from "./member/MemberTab";
import LeaderTab from "./leader/LeaderTab";
import RuleTab from "./rule/RuleTab";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import { fetchWithExtraOptions, tgxApi } from "../../../app/tgxApi";
import {
    useDeleteTeamMutation,
    useGetAllAssignToMembersQuery,
    useGetTeamByIdQuery,
    useLazyGetPreviewOrderListQuery,
    useTeamLeadersQuery,
    useTeamMembersQuery,
    useTeamRulesQuery,
    useUpdateTeamMutation
} from "./teamsApi";
import Form from "../../../components/common/form/Form";
import InputField from "../../../components/common/form/InputField";
import { handleRTKQuery } from "../../../helpers/utils/RtkQueryUtils";
import { OptionType } from "../../../helpers/utils/StyleUtils";
import ButtonTray from "../../../components/common/form/ButtonTray";
import { Routes as AppRoutes } from "../../../routes";
import Preloader from "../../../components/common/Preloader";
import ModalButton from "../../../components/common/form/ModalButton";
import IconButton from "../../../components/common/form/IconButton";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import { FaTrash } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import OrderPreview from "./rule/OrderPreview";

const TeamDetailPage = () => {

    const { teamId } = useParams();
    const [crumbs, setCrumbs] = useState<any[]>([]);
    const navigate = useNavigate();

    const memberData = useTeamMembersQuery({ teamId: teamId });
    const leaderData = useTeamLeadersQuery({ teamId: teamId });
    const rulesData = useTeamRulesQuery({ teamId: teamId });
    const dispatch = useDispatch();

    // @ts-ignore
    fetchWithExtraOptions(tgxApi.endpoints.getTeamById, { teamId }, {
        redirectToErrorPage: true,
        skip: !teamId
    });

    const teamByIdQuery = useGetTeamByIdQuery(teamId);
    const [teamData, setTeamData] = useState<any>({});
    const [updateTeamApi, updateTeamApiResponse] = useUpdateTeamMutation();
    const [showButtons, setShowButtons] = useState<boolean>(false);
    const [deleteApi, { isLoading }] = useDeleteTeamMutation();
    const previewData = useSelector((state: any) =>
        Object.values(
            state.tgxApi.queries
        ).filter((entry: any) => entry?.endpointName === 'getPreviewOrderList')
    );

    useEffect(() => {
        dispatch(tgxApi.util.invalidateTags([
            "getPreviewOrderList",
        ]));
    }, []);

    useEffect(() => {
        const teamObj = teamByIdQuery?.data;
        if (teamObj) {
            setCrumbs([
                { content: "Home", url: "/home" },
                { content: "Teams", url: "/teams" },
                { content: teamObj?.teamName, url: "" }
            ]);

            document.title = `${teamObj?.teamName}`;
        }
    }, [teamByIdQuery?.data]);


    useEffect(() => {
        let obj = teamByIdQuery?.data;
        if (obj) {
            setTeamData({
                ...obj,
            })
        }
    }, [teamByIdQuery?.data]);


    const bottomTabs = useMemo(() => {
        const result: any[] = [];
        if (hasPermission(["TEAM-V"])) {
            result.push({
                label: 'Members',
                Component: MemberTab
            });
            result.push({
                label: 'Leaders',
                Component: LeaderTab
            });
            result.push({
                label: 'Rules',
                Component: RuleTab
            });


            // @ts-ignore
            if (previewData?.[0]?.data) {
                // @ts-ignore
                result.push({
                    // @ts-ignore
                    label: 'Preview Rules',
                    Component: OrderPreview
                });
            }
        }
        return result;
    }, [memberData?.data, leaderData?.data, rulesData?.data, previewData]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        let obj = {
            ...teamData,
            tenantId: teamData?.tenantId?.value,
            customerId: teamData?.customerId,
            permittedCustomers: (!teamId || !teamData?.permitAllSubordinateCustomers)
                ? teamData?.permittedCustomers?.map((el: any) => el?.value)
                : []
        };

        const createOrUpdateUser = async () => {

            await updateTeamApi({
                id: teamId,
                requestBody: obj,
            }).unwrap();
        };

        await handleRTKQuery(createOrUpdateUser, () => {
            setTeamData({});
            navigate(AppRoutes.Teams.path);
        });

    }
    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;
        setTeamData({
            ...teamData,
            [name]: value,
        })
        setShowButtons(true);

    };
    const handCancel = async (e?: React.MouseEvent<HTMLButtonElement>) => {
        e?.preventDefault();
        setShowButtons(false);
    };
    const handleDelete = async () => {
        if (teamId) {
            await deleteApi(teamId).unwrap();
        }
        navigate(AppRoutes.Teams.path)
    };
    return (
        <>
            {teamData === undefined || teamData?.id !== teamId ?
                <Preloader /> :
                <div className="px-4 py-2">
                    <div className={"justify-between sm:block md:flex lg:flex"}>
                        <Breadcrumb crumbs={crumbs} />

                        <div className={"justify-between sm:block md:flex lg:flex"}>
                            {hasPermission(["TEAM-D"]) &&
                                <ModalButton
                                    triggerButton={
                                        <IconButton
                                            icon={<FaTrash />}
                                            title={"Delete Teams"}
                                            hasBackground={true}
                                            className="mr-2"
                                        />
                                    }
                                    modal={
                                        <ConfirmationModal
                                            show={true}
                                            setShow={() => {
                                            }}
                                            title={"Delete Team"}
                                            bodyMessage={"Are you sure you want to delete this Team?"}
                                            onSubmit={() => handleDelete()}
                                            isLoading={isLoading}
                                        />
                                    }
                                />}
                            {hasPermission(["AUDIT-V"]) &&
                                <AuditableInfoButton obj={teamByIdQuery.data} />
                            }
                        </div>
                    </div>
                    <div className="flex justify-center mt-3">
                        <div className="bg-text-1  sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">

                            <Form onSubmit={handleSubmit} fields={<>
                                <div
                                    className="flex justify-between items-center pb-3 border-b border-[rgba(0,0,0,0.1)]">
                                    <h5 className="font-semibold text-surface-0">Team Detail
                                    </h5>
                                    {showButtons && hasPermission(["TEAM-U"]) && (
                                        <ButtonTray
                                            buttons={[
                                                {
                                                    buttonProps: {
                                                        btnText: "Cancel",
                                                        onClick: () => handCancel(),
                                                        type: "close",
                                                    },
                                                    buttonType: "button"
                                                },
                                                {
                                                    buttonProps: {
                                                        btnText: "Update",
                                                        type: "submit",
                                                        isLoading: (updateTeamApiResponse.isLoading),
                                                    },
                                                    buttonType: "button",
                                                }
                                            ]}
                                            align="end"
                                            gap={2}
                                        />
                                    )}
                                </div>
                                <div className="grid grid-cols-12 text-sm">
                                    <InputField
                                        name={"teamName"}
                                        label="Name"
                                        onChange={handleOnChange}
                                        value={teamData?.teamName || ""}
                                        type="text"
                                        placeholder="Enter Team Name..."
                                        required={true}
                                        disabled={hasPermission(["TEAM-U"]) === false}
                                    />
                                    <InputField
                                        placeholder='Select Tenant...'
                                        value={teamData?.tenant}
                                        onChange={handleOnChange}
                                        label={"Tenant"}
                                        required={true}
                                        disabled
                                    />
                                </div>
                            </>}
                            />
                        </div>
                    </div>
                    <Tabs tabs={bottomTabs} className="bootstrap-tabs-component mt-5" />
                </div>
            }
        </>
    )
}

export default TeamDetailPage;